import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Play } from 'lucide-react';
import { api, ApiError } from '../api/client';
import type { Connection, QueryResult } from '../api/client';
import { Alert } from '../components/Feedback';

const EXAMPLES: Record<string, string> = {
  postgresql: 'SELECT table_name\nFROM information_schema.tables\nWHERE table_schema = \'public\'\nLIMIT 20;',
  mysql: 'SELECT table_name\nFROM information_schema.tables\nWHERE table_schema = DATABASE()\nLIMIT 20;',
  mariadb: 'SELECT table_name\nFROM information_schema.tables\nWHERE table_schema = DATABASE()\nLIMIT 20;',
  sqlserver: 'SELECT TOP 20 table_name\nFROM information_schema.tables\nWHERE table_type = \'BASE TABLE\';',
  sqlite: "SELECT name\nFROM sqlite_master\nWHERE type = 'table'\nLIMIT 20;",
};

export function QueryPage() {
  const [params] = useSearchParams();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [connectionId, setConnectionId] = useState(params.get('connection') ?? '');
  const [sql, setSql] = useState('SELECT 1;');
  const [result, setResult] = useState<QueryResult | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.databases
      .list()
      .then((items) => {
        setConnections(items);
        setConnectionId((current) => current || items[0]?.id || '');
        const chosen = items.find((c) => c.id === (current || items[0]?.id));
        if (chosen) setSql((current) => (current === 'SELECT 1;' ? EXAMPLES[chosen.database_type] ?? current : current));
      })
      .catch((e) => setError(e.message));
  }, []);

  const run = async () => {
    if (!connectionId) return;
    setBusy(true);
    setError('');
    setResult(null);
    try {
      setResult(await api.queries.run(connectionId, sql));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The query could not be run.');
    } finally {
      setBusy(false);
    }
  };

  const current = connections.find((c) => c.id === connectionId);

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <p className="eyebrow">QUERY</p>
          <h1>Query editor</h1>
          <p className="muted">
            Read-only. Writes and stacked statements are refused before they reach the database.
          </p>
        </div>
      </header>

      {error && <Alert tone="error" onDismiss={() => setError('')}>{error}</Alert>}

      <div className="query-layout">
        <section className="panel">
          <div className="field">
            <label htmlFor="connection">Database</label>
            <select
              id="connection"
              value={connectionId}
              onChange={(e) => {
                setConnectionId(e.target.value);
                const chosen = connections.find((c) => c.id === e.target.value);
                if (chosen) setSql(EXAMPLES[chosen.database_type] ?? 'SELECT 1;');
              }}
            >
              <option value="">Select a database</option>
              {connections.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="sql">SQL</label>
            <textarea
              id="sql"
              className="sql"
              value={sql}
              onChange={(e) => setSql(e.target.value)}
              spellCheck={false}
              rows={14}
            />
          </div>

          <button className="primary full" onClick={run} disabled={busy || !connectionId}>
            <Play size={16} /> {busy ? 'Running…' : 'Run query'}
          </button>
          <p className="muted small">
            Results are capped at 1,000 rows and the query is cancelled after 30 seconds.
          </p>
        </section>

        <section className="panel">
          <div className="panel-head">
            <h2>Results</h2>
            {result && (
              <span className="muted small">
                {result.row_count} rows · {result.execution_time_ms} ms
              </span>
            )}
          </div>

          {result?.truncated && (
            <Alert tone="info">
              {result.truncation_reason ?? 'The result was trimmed at the row or payload limit.'}
            </Alert>
          )}

          {result ? (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    {result.columns.map((column) => (
                      <th key={column}>{column}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex}>{cell === null ? <em className="muted">NULL</em> : String(cell)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="muted placeholder">
              {current
                ? 'Run a query to see structured results.'
                : 'Select a database to begin.'}
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
